import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Outlet,
  useBlocker,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useFeatureFlag, User } from "configcat-react";

// Components
// import FilterBar from '../../../components/Filters/FilterBar';
import StartARouteCard from "../../../components/Cards/StartARouteCard";
import HeaderBar from "../../../components/Header/HeaderBar";
import LabeledTextboxInput from "../../../components/Inputs/LabeledTextboxInput";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import MultipleChoiceModal from "../../../components/Modals/MultipleChoiceModal";
import NotificationModal from "../../../components/Modals/NotificationModal";

// Services
import axiosRoutes from "../../../services/axios/routes";
import axios from "../../../services/axios/backendAxios";
import EditProperties from "./RoutePage/EditProperties";
import AddPropertyWrapper from "./RoutePage/AddPropertyWrapper";
import ActiveRoutesBaseLayer from "../../../components/Maps/ActiveRoutes/BaseLayer";
import { RouteContext } from "./RoutePage/RouteContext";
import RouteColorSwatch from "../../../components/Maps/ActiveRoutes/RouteColorSwatch";
import { normalizeRouteColor } from "../../../components/Maps/ActiveRoutes/routeColor";
import axiosCompanyLocations from "../../../services/axios/companyLocations";
import { ObjectID } from "bson";
import Tabs from "../../../components/Navigation/Tabs";
import flash from "../../../services/flash";
import { formatErrorMessage } from "../../../services/axios/APIError";
import BlockerDialog from "../../../components/Modals/BlockerDialog";
import useDirtyState from "../../../hooks/useDirtyState";
import useDownloadHelper from "../../../hooks/useDownloadHelper";
import { pickOptions } from "../../../components/Modals/OptionsFormModal";
import CheckBox from "../../../components/Inputs/CheckBox";
import { addDays } from "date-fns";

// our dnd-kit currently has a hard-coded assumption that all
// sortable objects have a .id property that can be used for
// rearranging objects. ours don't. we setup both a job.id and make
// sure it handles duplicate jobs so they can be re-arranged without causing
// React rendering problems
export const remapWithSortableId = (jobs) => {
  let jobIdCounter = {};
  let mappedJobs = jobs.map((job) => {
    jobIdCounter[job._id] =
      jobIdCounter[job._id] !== undefined ? jobIdCounter[job._id] + 1 : 0;

    return {
      ...job,
      ...{
        id: job._id + "_" + jobIdCounter[job._id],
      },
    };
  });

  return mappedJobs;
};

const NULL_START_END_PAYLOAD = {
  id: null,
  location: null,
  name: null,
};

const UpdateRoutePage = (props) => {
  const navigate = useNavigate();
  let { routeId } = useParams();
  const isNew = routeId === "new";
  let [searchParams] = useSearchParams();
  const downloadPDF = useDownloadHelper("/route/print");

  const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
  const [displayNotificationModal, setDisplayNotificationModal] =
    useState(false);
  const [displayDeactivateModal, setDisplayDeactivateModal] = useState(false);
  const [headerLabel, setHeaderLabel] = useState("Route");
  const [loading, setLoading] = useState(!isNew);
  const [jobs, setJobs] = useState([]);
  const [routeChanged, setRouteChanged] = useState(false);
  // const [isDirty, setIsDirty] = useState(false);

  const [companyLocations, setCompanyLocations] = useState([]);

  const [routeLocations, setStartEnd] = useState({
    start: NULL_START_END_PAYLOAD,
    end: NULL_START_END_PAYLOAD,
  });

  // const [route, setRoute] = useState({
  //   color: "emerald",
  //   season: isNew ? searchParams.get("season") : undefined,
  // });
  const routeState = useDirtyState(
    {
      color: "emerald",
      season: isNew ? searchParams.get("season") : undefined,
    },
    false,
  );
  const route = routeState.value;
  const setRoute = routeState.modifyValue;
  const [showAddPropertyWindow, setShowAddPropertyWindow] = useState(false);
  // const [routeColor, setRouteColor] = useState(route.color);
  const [activeTab, setActiveTab] = useState("map");
  // const setRouteAndMarkDirty = useCallback(
  //   (p) => {
  //     setIsDirty(true);
  //     return setRoute(p);
  //   },
  //   [setIsDirty, setRoute],
  // );

  // eslint-disable-next-line no-unused-vars
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return false; // TODO: There is a bug with routeChanged. Need to update jobs state to be useDirtyState then this can be enabled.
    // eslint-disable-next-line no-unreachable
    console.log(routeState.isDirtyRef.current, routeChanged, nextLocation);
    if (
      (routeState.isDirtyRef.current || routeChanged) &&
      !nextLocation.pathname.startsWith(`/admin/route/${routeId}`)
    ) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      axios
        .get(`route/${routeId}?include=jobs`)
        .then((resp) => {
          setRoute({
            ...resp.data,
            color: resp.data.color == "undefined" ? "emerald" : resp.data.color,
          });
          setStartEnd({
            start: resp.data.start ?? NULL_START_END_PAYLOAD,
            end: resp.data.end ?? NULL_START_END_PAYLOAD,
          });
          setHeaderLabel(resp.data.name);
          let jobs = resp.data.jobs;
          let jobsWithMissingLocation = jobs.filter(
            (j) => j.location.lat == null || j.location.lng == null,
          );
          if (jobsWithMissingLocation.length > 0) {
            alert(
              `The following properties are missing a valid address.  Please go to the properties page and update the address to include them on a route: ${jobsWithMissingLocation.map((j) => j.name).join(", ")}`,
            );
          }
          setJobs(
            remapWithSortableId(
              jobs.filter(
                (j) => j.location.lat != null && j.location.lng != null,
              ),
            ),
          );
          setRouteChanged(false);
        })
        .finally(() => setLoading(false));
    }
  }, [isNew, routeId, setRoute]);

  useEffect(() => {
    axiosCompanyLocations.getAllLocations(
      {
        companyId: props.currentUser.currentCompanyId,
      },
      (data) => setCompanyLocations(data.locations),
    );
  }, [props.currentUser.currentCompanyId]);

  const handleSaveRoute = async () => {
    try {
      setLoading(true);

      // jobIds here sets our true order for jobs on the backend
      let jobIds = jobs.map((j) => j._id);

      // right now, as long as we only send back jobIds, we're fine
      // if we ever think about sending job data back raw from here
      // we need to strip out some of our book-keeping items from the
      // jobs payload (job.id and job.isNew)
      let drivingDirections = routeChanged
        ? {}
        : {
            drivingDistanceFormatted: route.drivingDistanceFormatted,
            drivingDistanceMeters: route.drivingDistanceMeters,
            drivingDurationSeconds: route.drivingDurationSeconds,
            drivingPath: route.drivingPath,
          };
      const payload = {
        ...{
          companyId: props.currentUser.currentCompanyId,
          details: route.details,
          name: route.name,
          color: route.color,
          start: routeLocations.start,
          end: routeLocations.end,
          jobIds,
          expiresAt: route.expiresAt,
        },
        ...drivingDirections,
      };
      if (isNew) {
        await axiosRoutes.createNewRoute(
          {
            ...{
              season: route.season,
            },
            ...payload,
          },
          (data) => {
            setLoading(false);
            routeState.clean(data.route);
            navigate("/admin/route");
          },
        );
      } else {
        const resp = await axios.post("route/update", {
          ...payload,
          ...{
            _id: routeId,
          },
        });
        flash.success("Route saved");
        routeState.clean(resp.data.route);
        navigate("/admin/route");
      }
    } catch (err) {
      flash.error(formatErrorMessage(err, "Issue saving route"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoute = () => {
    axiosRoutes.deleteRoute(
      { routeId: route._id },
      () => {
        setDisplayNotificationModal(true);
      },
      (err) => {
        alert(
          "Oops. Something went wrong while deleting this route. Please try again.",
        );
        console.log("Delete Route Error: ", err);
      },
    );
    return;
  };

  const handleUpdateRouteIsActive = () => {
    axiosRoutes.updateRoute(
      {
        _id: route._id,
        isActive: !route.isActive,
      },
      () => {
        setRoute({ ...route, isActive: !route.isActive });
        setLoading(false);
        setDisplayDeactivateModal(false);
      },
      (err) => {
        alert(
          "Oops. Something went wrong while deactivating this route. Please try again.",
        );
        console.log("Deactivate Route Error: ", err);
        setLoading(false);
      },
    );
  };

  const saveCompanyLocation = (name, type, location) => {
    let savedLocation = {
      companyId: props.currentUser.currentCompanyId,
      name,
      location: location.location,
      address: location.address,
      _id: new ObjectID().toString(),
    };
    axiosCompanyLocations.addLocation(savedLocation, () => {
      delete savedLocation.companyId;
      setCompanyLocations([...companyLocations, ...[savedLocation]]);
      setRouteLocation(type, savedLocation);
    });
  };
  const addJobs = (newJobs) => {
    // Filter out any invalid jobs as a defensive measure
    const validJobs = newJobs.filter((job) => job != null);
    // mutating these jobs with a property that the backend doesn't know about is a slight code smell
    validJobs.forEach((job) => (job.isNew = true));
    setJobsAndDirty([...jobs, ...validJobs]);
  };

  const setJobsAndDirty = (jobs) => {
    setRouteChanged(true);
    setJobs(remapWithSortableId(jobs));
  };

  const setOptimizedRoute = (optimizedRoute) => {
    let resortedJobs = optimizedRoute.jobs;
    setRouteChanged(false);
    setRoute({
      ...route,
      ...optimizedRoute,
    });

    const updatedJobIdOrder = remapWithSortableId(resortedJobs).map(
      (j) => j.id,
    );

    jobs.sort(
      (a, b) =>
        updatedJobIdOrder.indexOf(a.id) - updatedJobIdOrder.indexOf(b.id),
    );

    setJobs(jobs);
  };

  const setPreviewedRoute = (previewedRoute) => {
    setRouteChanged(false);
    setRoute({
      ...route,
      ...previewedRoute,
    });
  };

  // address can be a payload or null
  const setRouteLocation = (type, location) => {
    let newLocation = {};
    newLocation[type] = location ?? NULL_START_END_PAYLOAD;
    setStartEnd({
      ...routeLocations,
      ...newLocation,
    });
    setRouteChanged(true);
  };

  let buttons = [];
  if (!isNew) {
    buttons.push({
      label: "Delete",
      color: "red",
      onClick: () => setDisplayDeleteModal(true),
    });
    buttons.push({
      label: route.isActive ? "Make Inactive" : "Activate",
      color: "blue",
      onClick: () => setDisplayDeactivateModal(true),
    });
  }
  buttons.push({
    label: `${routeChanged || routeState.isDirty ? "*" : ""}Save`,
    color: "green",
    onClick: handleSaveRoute,
    disabled: !route.name || jobs.length < 1,
  });
  buttons.push({
    disabled: !route.season,
    label: `PDF`,
    color: "blue",
    onClick: async () => {
      const options = await pickOptions(null, {
        title: "PDF Options",
        formElements: (
          <>
            <CheckBox
              name="includeMap"
              label="Include Map"
              description="(increases download time)"
            />
          </>
        ),
      });
      downloadPDF({
        routeId: routeId,
        season: route.season,
        ...options,
      });
    },
  });

  const tabs = [
    { id: "map", name: "Route Map" },
    { id: "list", name: "Route List" },
  ];
  if (
    !loading &&
    !isNew &&
    props.currentUser &&
    props.currentUser.currentCompanyId !== route.companyId
  ) {
    return (
      <div className="flex flex-col items-center flex-1 min-h-full">
        <h2 className="text-lg mt-10 max-w-md">
          This property does not belong to the current company.
        </h2>
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-col items-center flex-1 min-h-full">
        <HeaderBar title={headerLabel} buttons={buttons} />
        <BlockerDialog blocker={blocker} />
        {loading ? (
          <LoadingIcon />
        ) : (
          <div className="flex w-full flex-1">
            <div
              className={`flex-1 flex-col w-full p-10 items-center overflow-y-scroll ${showAddPropertyWindow ? "hidden lg:flex" : "flex"}`}
            >
              <LabeledTextboxInput
                label="Name"
                placeholder="Name your route..."
                setValue={(val) => {
                  setRoute({ ...route, name: val });
                }}
                value={route.name}
              />
              <LabeledTextboxInput
                label="Description"
                placeholder="Tell us more about this route..."
                setValue={(val) => {
                  setRoute({ ...route, details: val });
                }}
                value={route.details}
              />

              <>
                <div className="w-full grid grid-cols-2">
                  <RouteColorSwatch
                    label="Route Color"
                    size="lg"
                    color={normalizeRouteColor(route.color)}
                    onChange={(val) => {
                      setRoute((current) => ({ ...current, color: val }));
                    }}
                    testId="route-color-swatch"
                  />
                  <CheckBox
                    checked={route.expiresAt != null}
                    label="One-time Route"
                    description="One-time routes are removed after 2 days with no activity. Useful for single-use routes."
                    onChange={(v) =>
                      setRoute((current) => ({
                        ...current,
                        expiresAt: v ? addDays(new Date(), 2) : null,
                      }))
                    }
                  />
                </div>
                <div className="self-start mb-4">
                  <Tabs
                    tabs={tabs}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                  />
                </div>
                {/* TODO: shift away from active routes base layer component? */}
                {activeTab === "map" ? (
                  <RouteContext.Provider
                    value={{
                      companyLocations,
                      route,
                      routeColor: route.color,
                      jobs,
                      routeChanged,
                      routeLocations,
                      addJobs,
                      saveNewLocation: saveCompanyLocation,
                      setJobs: setJobsAndDirty,
                      setOptimizedRoute: setOptimizedRoute,
                      setPreviewedRoute,
                      setRouteLocation,
                    }}
                  >
                    <div
                      className="relative"
                      style={{ width: "100%", height: "640px" }}
                    >
                      <ActiveRoutesBaseLayer>
                        <Outlet />
                      </ActiveRoutesBaseLayer>
                    </div>
                  </RouteContext.Provider>
                ) : (
                  <EditProperties
                    isNew={isNew}
                    jobs={jobs}
                    updateOrder={setJobsAndDirty}
                    setJobs={setJobs}
                    showAddPropertyWindow={showAddPropertyWindow}
                    setShowAddPropertyWindow={setShowAddPropertyWindow}
                  />
                )}
              </>

              <div className="flex items-start w-full mt-10">
                <StartARouteCard></StartARouteCard>
              </div>
            </div>
            {showAddPropertyWindow ? (
              <AddPropertyWrapper
                onCancel={() => setShowAddPropertyWindow(false)}
                active={showAddPropertyWindow}
                companyId={props.currentUser.currentCompanyId}
                season={route.season}
                setShowProperty={setShowAddPropertyWindow}
                existingJobs={jobs}
                concatJobs={(newJobs) => {
                  setJobs([...jobs, ...newJobs]);
                }}
              />
            ) : null}
          </div>
        )}
      </div>
      <MultipleChoiceModal
        open={displayDeleteModal}
        options={[
          {
            color: "red",
            label: "Delete Permanently",
            onClick: () => {
              setDisplayDeleteModal(false);
              handleDeleteRoute();
            },
          },
          {
            color: "blue",
            label: "Cancel",
            onClick: () => setDisplayDeleteModal(false),
          },
        ]}
        subtitle="Are you sure you want to delete this route? Warning, this is a permanent action."
        title="Delete"
      />
      <MultipleChoiceModal
        open={displayDeactivateModal}
        options={[
          {
            color: "green",
            label: route.isActive ? "Make Inactive" : "Activate Route",
            onClick: () => {
              setDisplayDeactivateModal(false);
              handleUpdateRouteIsActive();
            },
          },
          {
            color: "blue",
            label: "Cancel",
            onClick: () => setDisplayDeactivateModal(false),
          },
        ]}
        subtitle={
          route.isActive
            ? "If you deactivate this route employees will no longer be able to perform this route until you re-activate it. All data will be kept and you can re-activate this route at any time."
            : "Are you sure you want to activate this route? You will be able to see this route at the top of the list of routes while using the admin web portal."
        }
        title={route.isActive ? "Make Inactive" : "Activate Route"}
      />

      <NotificationModal
        button={{
          color: "blue",
          label: "Okay",
          onClick: () => {
            // Close and go back
            setDisplayNotificationModal(false);
            navigate(-1);
          },
        }}
        open={displayNotificationModal}
        subtitle="Congratulations! This route has been deleted."
        title="Deleted"
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(UpdateRoutePage);
export { UpdateRoutePage };
